import React from "react";
import { Dimensions, StyleSheet, Text, View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";

const { width: screenWidth, height: screenHeight } = Dimensions.get("window");

interface Card {
  id: number;
  title: string;
  subtitle?: string;
  backgroundColor: string;
}

interface SwipeableCardsProps {
  data: Card[];
  onSwipeLeft?: (card: Card) => void;
  onSwipeRight?: (card: Card) => void;
}

const CARD_WIDTH = screenWidth * 0.9;
const CARD_HEIGHT = screenHeight * 0.7;
const SWIPE_THRESHOLD = screenWidth * 0.25;
const MAX_ROTATION = 45;

const SwipeableCards: React.FC<SwipeableCardsProps> = ({
  data,
  onSwipeLeft,
  onSwipeRight,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const scale = useSharedValue(1);

  const handleSwipeLeft = (card: Card) => {
    onSwipeLeft?.(card);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleSwipeRight = (card: Card) => {
    onSwipeRight?.(card);
    setCurrentIndex((prev) => prev + 1);
  };

  const panGesture = Gesture.Pan()
    .onBegin(() => {
      scale.value = withSpring(1.05);
    })
    .onUpdate((event) => {
      translateX.value = event.translationX;
      translateY.value = event.translationY * 0.1;
    })
    .onEnd(() => {
      const shouldSwipe = Math.abs(translateX.value) > SWIPE_THRESHOLD;

      if (shouldSwipe) {
        const direction = translateX.value > 0 ? 1 : -1;
        const exitX = direction * screenWidth * 1.5;

        translateX.value = withTiming(exitX, { duration: 300 });
        translateY.value = withTiming(0, { duration: 300 });
        scale.value = withTiming(0.8, { duration: 300 });

        const currentCard = data[currentIndex];
        if (currentCard) {
          if (direction === 1) {
            runOnJS(handleSwipeRight)(currentCard);
          } else {
            runOnJS(handleSwipeLeft)(currentCard);
          }
        }
      } else {
        translateX.value = withSpring(0);
        translateY.value = withSpring(0);
        scale.value = withSpring(1);
      }
    });

  React.useEffect(() => {
    translateX.value = 0;
    translateY.value = 0;
    scale.value = 1;
  }, [currentIndex, translateX, translateY, scale]);

  const cardStyle = useAnimatedStyle(() => {
    const rotation = interpolate(
      translateX.value,
      [-screenWidth / 2, 0, screenWidth / 2],
      [-MAX_ROTATION, 0, MAX_ROTATION]
    );

    return {
      transform: [
        { translateX: translateX.value },
        { translateY: translateY.value },
        { rotate: `${rotation}deg` },
        { scale: scale.value },
      ],
    };
  });

  const nextCardStyle = useAnimatedStyle(() => {
    const nextScale = interpolate(
      Math.abs(translateX.value),
      [0, SWIPE_THRESHOLD],
      [0.95, 1]
    );

    return {
      transform: [{ scale: nextScale }],
      opacity: interpolate(
        Math.abs(translateX.value),
        [0, SWIPE_THRESHOLD],
        [0.8, 1]
      ),
    };
  });

  const renderCard = (card: Card, index: number) => {
    const isCurrentCard = index === currentIndex;
    const isNextCard = index === currentIndex + 1;

    if (index < currentIndex || index > currentIndex + 1) {
      return null;
    }

    return (
      <GestureDetector
        key={card.id}
        gesture={isCurrentCard ? panGesture : Gesture.Pan()}
      >
        <Animated.View
          style={[
            styles.card,
            { backgroundColor: card.backgroundColor },
            isCurrentCard ? cardStyle : {},
            isNextCard ? [styles.nextCard, nextCardStyle] : {},
          ]}
        >
          <Text style={styles.cardTitle}>{card.title}</Text>
          {card.subtitle && (
            <Text style={styles.cardSubtitle}>{card.subtitle}</Text>
          )}
        </Animated.View>
      </GestureDetector>
    );
  };

  if (currentIndex >= data.length) {
    return (
      <View style={styles.container}>
        <Text style={styles.noMoreCards}>No more cards!</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {data.map((card, index) => renderCard(card, index))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    position: "absolute",
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    borderRadius: 20,
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
  },
  nextCard: {
    zIndex: -1,
  },
  cardTitle: {
    fontSize: 28,
    fontWeight: "bold",
    color: "white",
    textAlign: "center",
    marginBottom: 10,
  },
  cardSubtitle: {
    fontSize: 18,
    color: "rgba(255, 255, 255, 0.8)",
    textAlign: "center",
  },
  noMoreCards: {
    fontSize: 24,
    color: "#666",
    textAlign: "center",
  },
});

export default SwipeableCards;
